// The public docs (www.filmsquare.ai/docs, built from apps/site/docs) and which page explains which screen. Pure.
// A page's address is <base>/<section>/<slug>/ in English and <base>/<lang>/<section>/<slug>/ in another language.
// The tables below are typed as complete Records, so a new route or department does not compile until it has a
// page; test/docs.test.ts checks that every page named here exists in apps/site/docs/en.
import type { Department } from "./departments.ts";
import { baseLanguage, DEFAULT_LOCALE } from "./locale.ts";
import type { Loc, Route, SettingsPage } from "./route.ts";

export const DEFAULT_DOCS_URL = "https://www.filmsquare.ai/docs";

/** The languages the docs are published in (apps/site/site.config.ts `docs.languages`); English is unprefixed. */
export const DOCS_LOCALES: readonly string[] = ["en", "nl", "de", "fr", "es"];

/** A docs page as `<section>/<slug>`; "" is the docs home. */
export type DocsPath = string;

const envBase = (): string | undefined => {
  try {
    return import.meta.env?.VITE_DOCS_URL || undefined;
  } catch {
    return undefined;
  }
};

/**
 * The address of a docs page in a language. A language the docs do not have falls back to English, so the link
 * never leads to a missing page. `base` defaults to VITE_DOCS_URL, else www.filmsquare.ai/docs.
 */
export function docsUrl(path: DocsPath, locale: string = DEFAULT_LOCALE, base?: string): string {
  const root = (base ?? envBase() ?? DEFAULT_DOCS_URL).replace(/\/+$/, "");
  const lang = baseLanguage(locale);
  const prefix = lang && lang !== "en" && DOCS_LOCALES.includes(lang) ? `/${lang}` : "";
  const page = path.replace(/^\/+|\/+$/g, "");
  return `${root}${prefix}/${page ? `${page}/` : ""}`;
}

/** "Your first production", linked from Learn. */
export const FIRST_PRODUCTION_DOCS: DocsPath = "getting-started/first-production";

/** "Import from Higgsfield": the import modal, its entry cards and the run screen. */
export const IMPORT_DOCS: DocsPath = "claude/import-from-higgsfield";

/** The page for each department of the board. The extras share one page. */
export const DEPT_DOCS: Readonly<Record<Department, DocsPath>> = {
  writing: "departments/writing",
  breakdown: "departments/breakdown",
  casting: "departments/casting",
  wardrobe: "departments/wardrobe",
  sets: "departments/sets",
  props: "departments/props",
  animatic: "departments/animatic",
  draft: "departments/draft",
  final: "departments/final",
  voice: "departments/extra-departments",
  music: "departments/extra-departments",
  sound: "departments/extra-departments",
  edit: "departments/extra-departments",
};

/** The page for each place inside a production. An episode without a department picks the departments page. */
export const LOC_DOCS: Readonly<Record<Loc["kind"], DocsPath>> = {
  root: "concepts/structure",
  episodes: "concepts/structure",
  episode: "concepts/departments",
  scene: "concepts/structure",
  cut: "working-together/the-cut",
  storyboard: "departments/animatic",
  tools: "concepts/takes",
  review: "working-together/notes-and-review",
  blocking: "concepts/structure",
  tags: "departments/breakdown",
  elements: "concepts/elements",
  canvases: "working-together/canvas",
  assets: "working-together/folders",
  makers: "working-together/assignments",
  folder: "working-together/folders",
  crew: "working-together/crew-and-roles",
  settings: "account/workspace-settings",
};

export const SETTINGS_DOCS: Readonly<Record<SettingsPage, DocsPath>> = {
  members: "account/workspace-settings",
  claude: "claude/connect",
  dailies: "account/workspace-settings",
  connections: IMPORT_DOCS,
};

/** The page for each screen outside a production. Explore, Learn and the odd ones go to the docs home. */
export const VIEW_DOCS: Readonly<Record<Exclude<Route["view"], "project" | "settings">, DocsPath>> = {
  home: "getting-started/the-studio",
  productions: "concepts/structure",
  new: "getting-started/first-production",
  create: "concepts/takes",
  elements: "concepts/elements",
  "workspace-element": "concepts/elements",
  explore: "",
  book: "",
  learn: "",
  credits: "account/plans-and-credits",
  signoff: "working-together/whats-waiting",
  import: IMPORT_DOCS,
  shot: "concepts/takes",
  element: "concepts/elements",
  canvas: "working-together/canvas",
  connect: "claude/connect",
  invite: "working-together/crew-and-roles",
  share: "working-together/the-cut",
  "dailies-unsubscribe": "account/workspace-settings",
  legacy: "",
};

/** The docs page that best explains the screen of a route. */
export function docsPathFor(route: Route): DocsPath {
  switch (route.view) {
    case "project":
      return route.loc.kind === "episode" && route.loc.dept
        ? DEPT_DOCS[route.loc.dept]
        : LOC_DOCS[route.loc.kind];
    case "settings":
      return SETTINGS_DOCS[route.page];
    default:
      return VIEW_DOCS[route.view];
  }
}

/** Every page the tables name (for the test that proves they exist). */
export function allDocsPaths(): DocsPath[] {
  return [
    ...new Set([
      ...Object.values(DEPT_DOCS),
      ...Object.values(LOC_DOCS),
      ...Object.values(SETTINGS_DOCS),
      ...Object.values(VIEW_DOCS),
      FIRST_PRODUCTION_DOCS,
    ]),
  ];
}
